import type { ReactNode } from 'react';

import { AttachmentsProvider } from './attachments-provider';
import { ChatAttachments } from './chat-attachments';
import { ChatAttachmentsPreview } from './chat-attachments-preview';
import { ChatInput } from './chat-input';
import { ChatInputProvider, DragHandlersProvider } from './chat-input-context';
import { ChatInputTextarea } from './chat-input-textarea';
import { ChatInputToolbar } from './chat-input-toolbar';
import { DragOverlay } from './drag-overlay';
import { SendButton } from './send-button';
import { useChatInputSetup } from './use-chat-input-setup';
import { WebSearchWarning } from './web-search-warning';

interface ChatInputContainerProps {
  placeholder?: string;
  className?: string;
  /**
   * Custom toolbar content. If provided, replaces the default toolbar.
   * If not provided, uses default toolbar with attachments and send button.
   */
  toolbar?: ReactNode;
}

/**
 * Default chat input container that sets up context and provides the standard composition.
 * Apps can customize the toolbar by passing a custom toolbar prop.
 *
 * For more advanced customization, use the `useChatInputSetup` hook directly.
 *
 * @example Default usage:
 * ```tsx
 * <ChatInputContainer />
 * ```
 *
 * @example Custom toolbar:
 * ```tsx
 * <ChatInputContainer
 *   toolbar={
 *     <ChatInputToolbar>
 *       <CustomButton />
 *       <SendButton />
 *     </ChatInputToolbar>
 *   }
 * />
 * ```
 *
 * @example Without attachments:
 * ```tsx
 * <ChatInputContainer
 *   toolbar={
 *     <ChatInputToolbar>
 *       <SendButton />
 *     </ChatInputToolbar>
 *   }
 * />
 * ```
 *
 * @example Full customization with hook:
 * ```tsx
 * function MyCustomInput() {
 *   const contextValue = useChatInputSetup();
 *
 *   return (
 *     <ChatInputProvider value={contextValue}>
 *       <ChatInput>
 *         <ChatInputTextarea />
 *         <ChatInputToolbar>
 *           <MyCustomActions />
 *           <SendButton />
 *         </ChatInputToolbar>
 *       </ChatInput>
 *     </ChatInputProvider>
 *   );
 * }
 * ```
 */
export function ChatInputContainer({
  placeholder = 'How can I help you today?',
  className,
  toolbar,
}: ChatInputContainerProps) {
  const contextValue = useChatInputSetup();

  const defaultToolbar = (
    <ChatInputToolbar>
      <div className="flex flex-1">
        <ChatAttachments />
      </div>
      <SendButton />
    </ChatInputToolbar>
  );

  return (
    <AttachmentsProvider>
      <DragHandlersProvider>
        <ChatInputProvider value={contextValue}>
          <ChatInput className={className}>
            <DragOverlay />
            <ChatInputTextarea placeholder={placeholder} />
            {toolbar ?? defaultToolbar}
            <ChatAttachmentsPreview />
            <WebSearchWarning />
          </ChatInput>
        </ChatInputProvider>
      </DragHandlersProvider>
    </AttachmentsProvider>
  );
}
